import Link from 'next/link';
import { FC, Fragment } from 'react';
import ExpandMoreBoldIcon from 'src/app.core/linkareer/svgIcon/ExpandMoreBoldIcon';
import styled from 'styled-components';
import ChannelUnavailableModal from '../Modal/ChannelUnavailableModal';
import UserLoginMenu from './UserLoginMenu';
import useHasNewApplications from './hook/useHasNewApplications';
import useUserActionMenu, { menuType } from './hook/useUserActionMenu';
import { CurrentUserQueryResult, UserType } from 'src/app.core/linkareer/types/gql.types';

interface UserHeaderLoginActionsProps {
  currentUser?: CurrentUserQueryResult['currentUser'];
  loading?: boolean;
}

const UserHeaderLoginActions: FC<UserHeaderLoginActionsProps> = ({
  currentUser,
  loading = false,
}) => {
  const { hasNewApplications } = useHasNewApplications({
    skip: currentUser?.type !== UserType.NORMAL,
  });

  const { userMenuList, channelUnavailable, onCloseModal } = useUserActionMenu(currentUser, {
    hasNewApplications,
  });

  if (loading) return <></>;
  if (!currentUser) return <UserLoginMenu />;

  return (
    <StyledWrapper>
      <div className="user-box">
        <div className="user-name">{currentUser?.username}</div>
        <ExpandMoreBoldIcon className="expand-more-icon" />
      </div>
      <ul className="user-menu-list">
        {userMenuList.map((menuItem, index) => (
          <Fragment key={`${menuItem.label}-id-${index}`}>
            {menuItem.type === menuType.EXTERNAL_LINK && (
              <li>
                <a
                  key={menuItem.label}
                  href={menuItem.href}
                  className="user-menu-item"
                  data-new={menuItem.isNew}
                  data-show-badge={menuItem.showBadge}
                  {...menuItem.attributes}
                >
                  {menuItem.label}
                </a>
              </li>
            )}
            {menuItem.type === menuType.INTERNAL_LINK && (
              <li>
                <Link
                  key={menuItem.label}
                  href={menuItem.href as string}
                  className="user-menu-item"
                  data-new={menuItem.isNew}
                  data-show-badge={menuItem.showBadge}
                  {...menuItem.attributes}
                >
                  {menuItem.label}
                </Link>
              </li>
            )}
            {menuItem.type === menuType.BUTTON && (
              <li>
                <p
                  className="user-menu-item"
                  data-show-badge={menuItem.showBadge}
                  onClick={menuItem.onClick}
                >
                  {menuItem.label}
                </p>
              </li>
            )}
          </Fragment>
        ))}
      </ul>
      {channelUnavailable && <ChannelUnavailableModal onClickCloseModal={onCloseModal} />}
    </StyledWrapper>
  );
};

export default UserHeaderLoginActions;

const StyledWrapper = styled.div`
  position: relative;
  cursor: pointer;

  &:hover {
    .user-menu-list {
      display: block;
    }
  }

  .user-box {
    display: flex;
    align-items: center;

    .user-name {
      font-size: 14px;
      max-width: 124px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .expand-more-icon {
      font-size: 10px;
      padding-top: 1px;
      margin-left: 8px;
    }
  }

  .user-menu-list {
    width: 135px;
    padding: 5px 0px;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 5px;
    box-shadow: 3px 3px 3px 0 rgba(0 0 0 / 15%);
    position: absolute;
    z-index: 10;
    top: 30px;
    left: -30px;
    display: none;

    &::before {
      content: '';
      width: 100%;
      height: 30px;
      display: block;
      position: absolute;
      top: -30px;
    }

    & > li {
      width: 100%;
      height: 27.5px;

      :hover {
        background-color: #eeeeee;
      }

      & > a,
      p {
        width: 100%;
        height: 100%;
        font-size: 12px;
        font-weight: 500;
        padding: 5px 15px;
        color: #333333;
        display: block;
        position: relative;
        white-space: nowrap;

        &[data-show-badge='true'] {
          &::after {
            content: '';
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: #ef2929;
            position: absolute;
            top: 3px;
            left: 60%;
          }
        }

        &[data-new='true'] {
          background-color: #edf6ff;
          color: #01a0ff;

          &:hover {
            background-color: #d8ebff;
          }

          &::after {
            content: 'NEW';
            display: inline-block;
            margin-left: 5px;
            padding: 2px 4px;
            border-radius: 3px;
            background-color: #01a0ff;
            color: #ffffff;
            font-size: 8px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.3px;
            vertical-align: 1px;
          }
        }
      }
    }
  }
`;
